import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { Check } from '@zcode-ui/icons'
import { cn } from '../../lib/utils'

const checkboxVariants = cva(
  'bg-transparent peer h-4 w-4 shrink-0 rounded-sm border border-solid border-gray-250 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-pink-600 data-[state=checked]:border-pink-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring focus-visible:border-neutral-500',
  {
    variants: {
      size: {
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const labelTextVariants = cva('ml-2 select-none', {
  variants: {
    size: {
      md: 'text-base leading-6',
      lg: 'text-lg leading-[30px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

interface CheckboxComponentProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  textClassName?: string
}

const CheckboxComponent = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxComponentProps
>(({ className, children, size, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ size, className }))}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('flex items-center justify-center text-current')}
    >
      <Check fill="#fff" size={size === 'lg' ? 12 : 8} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxComponentProps
>(({ children, textClassName, size, ...props }, ref) => (
  <>
    <label className="inline-flex cursor-pointer items-center">
      <CheckboxComponent ref={ref} size={size} {...props} />
      <span
        className={cn(labelTextVariants({ size, className: textClassName }))}
      >
        {children}
      </span>
    </label>
  </>
))

export { Checkbox }
